import { useCallback, useEffect, useRef } from 'react';

import { useFeatureIsOn } from 'src/common/growthbook';
import { extendedApiSlice, useGetUserQuery } from 'src/common/slices';
import { useEnrollInPathMutation } from 'src/features/path-outline/path-outline.slice';
import { useAppDispatch } from 'src/app/hooks';
import { RTKQueryTag } from 'src/common/constants';

import { useGetWelcomePathsQuery } from '../welcome.slice';
import { WelcomePath } from '../welcome.types';

export const useDelayedPathEnrollmentExperiment = () => {
  const [enrollInPathMutation] = useEnrollInPathMutation();
  const isFeatureOn = useFeatureIsOn('delay-path-enrollment');
  const { data: userData } = useGetUserQuery();
  const isDelayedPathEnrollmentEnabled = isFeatureOn && userData?.data && !userData?.data?.user.local.path;
  const { data: welcomePathsData } = useGetWelcomePathsQuery(
    { skipRedirect: true },
    {
      skip: !isDelayedPathEnrollmentEnabled,
    },
  );
  const recommendedPathRef = useRef<WelcomePath>();
  const isRoomReplacementExperimentEnabled = useFeatureIsOn('improvement-offensive-security-intro');

  useEffect(() => {
    let recommendedPath = welcomePathsData?.data?.[0];
    if (recommendedPath) {
      if (isRoomReplacementExperimentEnabled && recommendedPath.firstRoomCode === 'offensivesecurityintro') {
        recommendedPath = { ...recommendedPath, firstRoomCode: 'offensivesecurityintrokK' };
      }
      recommendedPathRef.current = recommendedPath;
    }
  }, [isRoomReplacementExperimentEnabled, welcomePathsData]);

  const dispatch = useAppDispatch();

  const enrollInRecommendedPath = useCallback(
    async (completedRoomCode: string) => {
      if (!recommendedPathRef.current?.code || recommendedPathRef?.current.firstRoomCode !== completedRoomCode) {
        return;
      }
      await enrollInPathMutation(recommendedPathRef.current?.code);
      dispatch(extendedApiSlice.util.invalidateTags([RTKQueryTag.Room]));
    },
    [dispatch, enrollInPathMutation],
  );

  return {
    isDelayedPathEnrollmentEnabled,
    enrollInRecommendedPath,
    recommendedPath: recommendedPathRef.current,
    isFeatureOn,
  };
};
